import React, { useState } from 'react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+47[\s]?)?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}[\s]?[0-9]{2}$|^(\+47[\s]?)?[0-9]{8}$/;

const ContactForm = ({ onSmsOptIn }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    smsOptIn: false,
    emailOptIn: false,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        return !value?.trim() ? 'Navn er påkrevd' : '';
      case 'email':
        if (!value?.trim()) return 'E-post er påkrevd';
        if (!EMAIL_REGEX?.test(value)) return 'Ugyldig e-postadresse (f.eks. navn@eksempel.no)';
        return '';
      case 'phone':
        if (value?.trim() && !PHONE_REGEX?.test(value?.trim())) return 'Ugyldig telefonnummer (f.eks. +47 000 00 000)';
        return '';
      case 'message':
        return !value?.trim() ? 'Melding er påkrevd' : '';
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (touched?.[name]) {
      setErrors(prev => ({ ...prev, [name]: validateField(name, newValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e?.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validate = () => {
    const errs = {};
    ['name', 'email', 'phone', 'message']?.forEach(field => {
      const err = validateField(field, formData?.[field]);
      if (err) errs[field] = err;
    });
    return errs;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setTouched({ name: true, email: true, phone: true, message: true });
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    if (formData?.smsOptIn && formData?.phone) {
      onSmsOptIn?.(formData?.phone);
      localStorage.setItem('contactFormPhone', formData?.phone);
    } else if (formData?.phone) {
      localStorage.setItem('contactFormPhone', formData?.phone);
    }
    setIsSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
          width: '64px', height: '64px',
          background: 'rgba(255,87,34,0.1)',
          border: '2px solid rgba(255,87,34,0.4)',
          borderRadius: '2px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 24px',
          transform: 'rotate(3deg)',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px', letterSpacing: '0.02em' }}>
          Melding sendt!
        </h3>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>
          Vi svarer vanligvis innen 1–2 virkedager.
        </p>
        {formData?.smsOptIn && (
          <div style={{ marginTop: '20px', padding: '12px 20px', background: 'rgba(255,87,34,0.08)', border: '1px solid rgba(255,87,34,0.2)', borderRadius: '2px', display: 'inline-block' }}>
            <p style={{ fontFamily: 'var(--font-caption)', fontSize: '13px', color: '#FF5722', fontWeight: 500 }}>
              📱 SMS-varsler aktivert for {formData?.phone}
            </p>
          </div>
        )}
      </div>
    );
  }

  const inputStyle = (hasError) => ({
    width: '100%',
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(224,228,232,0.12)',
    borderBottom: hasError ? '2px solid #FF5722' : '2px solid rgba(224,228,232,0.25)',
    borderRadius: '0',
    padding: '14px 16px',
    fontFamily: 'var(--font-body)',
    fontSize: '15px',
    fontWeight: 400,
    color: 'var(--color-foreground)',
    outline: 'none',
    transition: 'border-color 200ms ease',
    boxSizing: 'border-box',
  });

  const labelStyle = {
    fontFamily: 'var(--font-heading)',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: 'var(--color-muted-foreground)',
    display: 'block',
    marginBottom: '8px',
  };

  const errorStyle = {
    fontFamily: 'var(--font-caption)',
    fontSize: '12px',
    color: '#FF5722',
    marginTop: '4px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Name + Email row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>Navn *</label>
          <input
            name="name"
            value={formData?.name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Ditt fulle navn"
            style={inputStyle(errors?.name)}
            onFocus={e => { if (!errors?.name) e.target.style.borderBottomColor = '#FF5722'; }}
          />
          {errors?.name && <p style={errorStyle}><span>⚠</span>{errors?.name}</p>}
        </div>
        <div>
          <label style={labelStyle}>E-post *</label>
          <input
            name="email"
            type="email"
            value={formData?.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="din@epost.no"
            style={inputStyle(errors?.email)}
            onFocus={e => { if (!errors?.email) e.target.style.borderBottomColor = '#FF5722'; }}
          />
          {errors?.email && <p style={errorStyle}><span>⚠</span>{errors?.email}</p>}
        </div>
      </div>
      {/* Phone + Subject row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div>
          <label style={labelStyle}>Telefon (valgfritt)</label>
          <input
            name="phone"
            value={formData?.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="+47 000 00 000"
            style={inputStyle(errors?.phone)}
            onFocus={e => { if (!errors?.phone) e.target.style.borderBottomColor = '#FF5722'; }}
          />
          {errors?.phone && <p style={errorStyle}><span>⚠</span>{errors?.phone}</p>}
        </div>
        <div>
          <label style={labelStyle}>Emne</label>
          <input
            name="subject"
            value={formData?.subject}
            onChange={handleChange}
            placeholder="Hva gjelder henvendelsen?"
            style={inputStyle(false)}
            onFocus={e => e.target.style.borderBottomColor = '#FF5722'}
            onBlur={e => e.target.style.borderBottomColor = 'rgba(224,228,232,0.25)'}
          />
        </div>
      </div>
      {/* Message */}
      <div style={{ marginBottom: '28px' }}>
        <label style={labelStyle}>Melding *</label>
        <textarea
          name="message"
          value={formData?.message}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Skriv din melding her..."
          rows={5}
          style={{ ...inputStyle(errors?.message), resize: 'vertical', minHeight: '120px' }}
          onFocus={e => { if (!errors?.message) e.target.style.borderBottomColor = '#FF5722'; }}
        />
        {errors?.message && <p style={errorStyle}><span>⚠</span>{errors?.message}</p>}
      </div>
      {/* SMS Opt-in */}
      <div style={{
        marginBottom: '20px',
        padding: '20px 24px',
        background: formData?.smsOptIn ? 'rgba(255,87,34,0.08)' : 'rgba(255,255,255,0.02)',
        border: `2px solid ${formData?.smsOptIn ? 'rgba(255,87,34,0.35)' : 'rgba(224,228,232,0.1)'}`,
        borderRadius: '2px',
        transition: 'all 250ms ease',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '20px', height: '20px', borderTop: '3px solid #FF5722', borderRight: '3px solid #FF5722' }} />
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: 'pointer' }}>
          <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
            <input
              type="checkbox"
              name="smsOptIn"
              checked={formData?.smsOptIn}
              onChange={handleChange}
              style={{ position: 'absolute', opacity: 0, width: '22px', height: '22px', cursor: 'pointer', zIndex: 1 }}
            />
            <div style={{
              width: '22px', height: '22px',
              background: formData?.smsOptIn ? '#FF5722' : 'transparent',
              border: `2px solid ${formData?.smsOptIn ? '#FF5722' : 'rgba(224,228,232,0.3)'}`,
              borderRadius: '2px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 200ms ease',
            }}>
              {formData?.smsOptIn && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <div>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 600, color: formData?.smsOptIn ? '#FF5722' : 'var(--color-foreground)', marginBottom: '4px', transition: 'color 200ms ease' }}>
              📱 Ja, send meg SMS-oppdateringer
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
              Motta eksklusive tilbud, bomtakst-varsler og tjenesteoppdateringer via SMS. Maks 2 meldinger per uke. Svar STOPP for å avslutte.
            </p>
          </div>
        </label>
      </div>

      {/* Email Opt-in */}
      <div style={{
        marginBottom: '32px',
        padding: '20px 24px',
        background: formData?.emailOptIn ? 'rgba(0,79,152,0.08)' : 'rgba(255,255,255,0.02)',
        border: `2px solid ${formData?.emailOptIn ? 'rgba(0,79,152,0.35)' : 'rgba(224,228,232,0.1)'}`,
        borderRadius: '2px',
        transition: 'all 250ms ease',
        position: 'relative',
      }}>
        <div style={{ position: 'absolute', top: '-1px', right: '-1px', width: '20px', height: '20px', borderTop: '3px solid #004F98', borderRight: '3px solid #004F98' }} />
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: 'pointer' }}>
          <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
            <input
              type="checkbox"
              name="emailOptIn"
              checked={formData?.emailOptIn}
              onChange={handleChange}
              style={{ position: 'absolute', opacity: 0, width: '22px', height: '22px', cursor: 'pointer', zIndex: 1 }}
            />
            <div style={{
              width: '22px', height: '22px',
              background: formData?.emailOptIn ? '#004F98' : 'transparent',
              border: `2px solid ${formData?.emailOptIn ? '#004F98' : 'rgba(224,228,232,0.3)'}`,
              borderRadius: '2px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'all 200ms ease',
            }}>
              {formData?.emailOptIn && (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <div>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: '15px', fontWeight: 600, color: formData?.emailOptIn ? '#004F98' : 'var(--color-foreground)', marginBottom: '4px', transition: 'color 200ms ease' }}>
              ✉️ Ja, send meg e-postoppdateringer
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
              Motta nyhetsbrev, produktoppdateringer og eksklusive tilbud på e-post. Du kan melde deg av når som helst.
            </p>
          </div>
        </label>
      </div>
      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        style={{
          width: '100%',
          background: isSubmitting ? 'rgba(255,87,34,0.6)' : '#FF5722',
          border: '2px solid #FF5722',
          borderRadius: '0',
          padding: '16px 32px',
          fontFamily: 'var(--font-heading)',
          fontSize: '15px',
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: '#FFFFFF',
          cursor: isSubmitting ? 'not-allowed' : 'pointer',
          transition: 'all 250ms ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          position: 'relative',
          clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 12px 100%, 0 calc(100% - 12px))',
        }}
        onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.background = '#E64A19'; }}
        onMouseLeave={e => { if (!isSubmitting) e.currentTarget.style.background = '#FF5722'; }}
      >
        {isSubmitting ? (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
            Sender...
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Send melding
          </>
        )}
      </button>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        input::placeholder, textarea::placeholder { color: rgba(156, 163, 175, 0.5); }
      `}</style>
    </form>
  );
};

export default ContactForm;
